import React, { useState, useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import {
  SparklesIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';

interface AnalysisFinding {
  severity: 'error' | 'warning' | 'info';
  category: string;
  message: string;
  line_item_id?: string;
  line_item_description?: string;
  suggestion?: string;
}

interface BillAnalysis {
  bill_id: string;
  quality_score: number;
  findings: AnalysisFinding[];
  recommendations: string[];
  summary: {
    total_findings: number;
    errors: number;
    warnings: number;
    info: number;
  };
  analyzed_at: string;
}

interface Props {
  billId: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  vague_description: 'Vague Description',
  short_description: 'Short Description',
  block_billing: 'Block Billing',
  excessive_hours: 'Excessive Hours',
  impossible_hours: 'Impossible Hours',
  duplicate_entry: 'Duplicate Entry',
  missing_utbms: 'Missing UTBMS',
  rate_outlier: 'Rate Outlier',
  zero_amount: 'Zero Amount',
  guideline_rate_cap: 'Rate Cap',
  guideline_max_hours: 'Max Hours',
  guideline_min_description: 'Description Length',
  guideline_block_billing: 'Block Billing (Guideline)',
  guideline_prohibited_code: 'Prohibited Code',
  guideline_prohibited_keyword: 'Prohibited Keyword',
  guideline_require_utbms: 'UTBMS Required',
};

const SeverityIcon: React.FC<{ severity: string }> = ({ severity }) => {
  switch (severity) {
    case 'error':
      return <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0" />;
    case 'warning':
      return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 flex-shrink-0" />;
    default:
      return <InformationCircleIcon className="h-5 w-5 text-blue-500 flex-shrink-0" />;
  }
};

const PreBillReviewPanel: React.FC<Props> = ({ billId }) => {
  const { keycloak } = useKeycloak();
  const [analysis, setAnalysis] = useState<BillAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runAnalysis = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/billing/bills/${billId}/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${keycloak?.token || ''}`,
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.description || 'Analysis failed.');
      }
      const body = await res.json();
      setAnalysis(body.data);
    } catch (err: any) {
      setError(err.message || 'Failed to analyze bill.');
    } finally {
      setLoading(false);
    }
  }, [billId, keycloak?.token]);

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 dark:text-green-400';
    if (score >= 70) return 'text-yellow-600 dark:text-yellow-400';
    if (score >= 50) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 90) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 50) return 'Needs Improvement';
    return 'Poor';
  };

  const getGaugeBg = (score: number) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 70) return 'bg-yellow-500';
    if (score >= 50) return 'bg-orange-500';
    return 'bg-red-500';
  };

  if (!analysis) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-2 mb-4">
          <SparklesIcon className="h-5 w-5 text-purple-500" />
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">AI Pre-Bill Review</h3>
        </div>
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>
        )}
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Run an intelligent analysis to check for vague descriptions, block billing, excessive hours,
          duplicate entries, rate anomalies, and guideline compliance.
        </p>
        <button
          onClick={runAnalysis}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
        >
          {loading ? (
            <>
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <SparklesIcon className="h-4 w-4" />
              Run AI Review
            </>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Score + Summary Row */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <SparklesIcon className="h-5 w-5 text-purple-500" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">AI Pre-Bill Review</h3>
          </div>
          <button
            onClick={runAnalysis}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <ArrowPathIcon className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Re-analyze
          </button>
        </div>

        <div className="flex items-center gap-6">
          {/* Score gauge */}
          <div className="flex-shrink-0">
            <div className="relative w-20 h-20">
              <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="34" fill="none" stroke="currentColor" strokeWidth="6" className="text-gray-200 dark:text-gray-700" />
                <circle
                  cx="40" cy="40" r="34" fill="none" strokeWidth="6"
                  strokeDasharray={`${(analysis.quality_score / 100) * 213.6} 213.6`}
                  strokeLinecap="round"
                  className={getScoreColor(analysis.quality_score).replace('text-', 'stroke-')}
                  style={{ stroke: analysis.quality_score >= 90 ? '#22c55e' : analysis.quality_score >= 70 ? '#eab308' : analysis.quality_score >= 50 ? '#f97316' : '#ef4444' }}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-lg font-bold ${getScoreColor(analysis.quality_score)}`}>
                  {analysis.quality_score}
                </span>
              </div>
            </div>
            <div className={`text-xs text-center mt-1 font-medium ${getScoreColor(analysis.quality_score)}`}>
              {getScoreLabel(analysis.quality_score)}
            </div>
          </div>

          {/* Summary stats */}
          <div className="flex-1 grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-red-50 dark:bg-red-900/10 rounded-lg">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">{analysis.summary.errors}</div>
              <div className="text-xs text-red-600 dark:text-red-400">Errors</div>
            </div>
            <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-900/10 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{analysis.summary.warnings}</div>
              <div className="text-xs text-yellow-600 dark:text-yellow-400">Warnings</div>
            </div>
            <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/10 rounded-lg">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{analysis.summary.info}</div>
              <div className="text-xs text-blue-600 dark:text-blue-400">Info</div>
            </div>
          </div>
        </div>

        <div className="text-xs text-gray-400 dark:text-gray-500 mt-3">
          Analyzed at {new Date(analysis.analyzed_at).toLocaleString()}
        </div>
      </div>

      {/* Recommendations */}
      {analysis.recommendations.length > 0 && (
        <div className="bg-purple-50 dark:bg-purple-900/10 rounded-lg border border-purple-200 dark:border-purple-800 p-4">
          <div className="flex items-center gap-2 mb-2">
            <LightBulbIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            <h4 className="text-sm font-medium text-purple-800 dark:text-purple-300">Recommendations</h4>
          </div>
          <ul className="space-y-1">
            {analysis.recommendations.map((rec, i) => (
              <li key={i} className="text-sm text-purple-700 dark:text-purple-300 flex items-start gap-2">
                <span className="text-purple-400 mt-0.5">-</span>
                {rec}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Findings */}
      {analysis.findings.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
            Findings ({analysis.findings.length})
          </h4>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {analysis.findings.map((f, i) => (
              <div
                key={i}
                className={`p-3 rounded-lg border ${
                  f.severity === 'error'
                    ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800'
                    : f.severity === 'warning'
                    ? 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-200 dark:border-yellow-800'
                    : 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800'
                }`}
              >
                <div className="flex items-start gap-3">
                  <SeverityIcon severity={f.severity} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                        f.severity === 'error'
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                          : f.severity === 'warning'
                          ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                          : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                      }`}>
                        {CATEGORY_LABELS[f.category] || f.category}
                      </span>
                    </div>
                    <p className="text-sm text-gray-800 dark:text-gray-200">{f.message}</p>
                    {f.suggestion && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">
                        Suggestion: {f.suggestion}
                      </p>
                    )}
                    {f.line_item_description && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 truncate">
                        Line item: {f.line_item_description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-200 dark:border-green-800 p-4 flex items-center gap-2">
          <CheckCircleIcon className="h-5 w-5 text-green-500" />
          <span className="text-sm text-green-700 dark:text-green-300">
            No issues found. This bill passes all quality checks.
          </span>
        </div>
      )}
    </div>
  );
};

export default PreBillReviewPanel;
